/**
 * DripLayer.ts — floating coin sub-amount pops paced to cell_reveal frames.
 *
 * When a cell reveals a positive sub-amount, a little gold coin chip rises and
 * fades at the cell location, then a "+$x" value floats up. Zero-amount reveals
 * get a soft neutral ink ripple instead (loss-disguise, stays calm).
 */
import { Container, Graphics } from 'pixi.js';
import { makeText } from '../ui/widgets';
import { Tweens } from '../util/tween';
import { easeOutCubic, easeOutQuart, easeInOutCubic } from '../util/ease';
import theme, { hex } from '../render/theme';
import { money } from '../util/format';
import { Juice } from './Juice';

// CoinChip is a reusable {coin disc + $ glyph} container. We pool these so a long sweep that
// fires N drip pops doesn't allocate/destroy N Pixi objects per round (audit perf minor —
// "no pooling, dense reveal bursts allocate many Graphics, risks micro-stutter").
interface CoinChip { c: Container; coin: Graphics; glyph: ReturnType<typeof makeText>; r: number }

export class DripLayer extends Container {
  private chipPool: CoinChip[] = [];

  constructor() {
    super();
    this.eventMode = 'none';
  }

  private acquireChip(r: number, color: number): CoinChip {
    let chip = this.chipPool.pop();
    if (!chip) {
      const c = new Container();
      const coin = new Graphics();
      const glyph = makeText('$', { size: r * 0.95, weight: '800', color: theme.color.goldText });
      glyph.anchor.set(0.5); glyph.position.set(0, 1);
      c.addChild(coin, glyph);
      chip = { c, coin, glyph, r };
    }
    // (re)paint the disc — sizes may vary across reuses
    const ch = chip;
    ch.r = r;
    ch.coin.clear();
    ch.coin.circle(0, 0, r).fill(color);
    ch.coin.circle(0, 0, r * 0.82).fill(hex(theme.palette.gold1));
    ch.coin.circle(0, 0, r * 0.82).stroke({ width: 2, color: hex(theme.palette.gold2), alpha: 0.6 });
    ch.glyph.style.fontSize = r * 0.95;
    ch.c.alpha = 1;
    ch.c.scale.set(1);
    this.addChild(ch.c);
    return ch;
  }

  private releaseChip(chip: CoinChip): void {
    if (chip.c.parent) chip.c.parent.removeChild(chip.c);
    if (this.chipPool.length < 24) this.chipPool.push(chip); // cap so we don't grow unbounded
    else chip.c.destroy({ children: true });
  }

  /**
   * Pop a coin + value chip at (x,y). magnitude 0..1 scales size/intensity.
   * If opts.target is given, the coin ARCS to it (the balance pill) and fires opts.onArrive on
   * landing — so every win visibly flies to the wallet instead of fading in place.
   */
  coinPop(
    x: number, y: number, amount: number, magnitude: number, sparkle: boolean,
    opts?: { target?: { x: number; y: number }; onArrive?: () => void },
  ): void {
    if (opts?.target) { this.coinFlyTo(x, y, amount, magnitude, sparkle, opts.target, opts.onArrive); return; }
    const chip = new Container();
    chip.position.set(x, y);

    // coin disc
    const coin = new Graphics();
    const r = 13 + magnitude * 10;
    coin.circle(0, 0, r).fill(hex(theme.palette.gold2));
    coin.circle(0, 0, r * 0.82).fill(hex(theme.palette.gold1));
    coin.circle(0, 0, r * 0.82).stroke({ width: 2, color: hex(theme.palette.gold2), alpha: 0.6 });
    const c = makeText('$', { size: r * 0.95, weight: '800', color: theme.color.goldText });
    c.anchor.set(0.5);
    c.position.set(0, 1);
    chip.addChild(coin, c);

    // value text beside/above
    const label = makeText('+' + money(amount), {
      size: 15 + magnitude * 8,
      weight: '800',
      color: theme.color.goldText,
    });
    label.anchor.set(0.5);
    label.position.set(0, -r - 12);
    const labelBg = new Graphics();
    const lw = label.width + 18;
    const lh = label.height + 8;
    labelBg.roundRect(-lw / 2, -r - 12 - lh / 2, lw, lh, lh / 2).fill(hex(theme.palette.gold1));
    chip.addChildAt(labelBg, chip.children.length - 0);
    chip.addChild(label);

    this.addChild(chip);
    if (sparkle) Juice.glowPing(this, x, y, hex(theme.palette.gold1), r + 8);

    const rise = 46 + magnitude * 30;
    Tweens.to({
      from: 0,
      to: 1,
      duration: 760 + magnitude * 300,
      ease: easeOutQuart,
      onUpdate: (t) => {
        chip.position.set(x, y - rise * t);
        chip.alpha = t < 0.15 ? t / 0.15 : t > 0.7 ? 1 - (t - 0.7) / 0.3 : 1;
        const s = 0.7 + Math.min(1, t * 4) * 0.3;
        chip.scale.set(s);
      },
      onComplete: () => chip.destroy({ children: true }),
    });
  }

  /** Coin arcs from the cell to the balance pill, then fires onArrive (the "win lands" beat). */
  private coinFlyTo(
    x: number, y: number, amount: number, magnitude: number, sparkle: boolean,
    target: { x: number; y: number }, onArrive?: () => void,
  ): void {
    const r = 12 + magnitude * 8;
    const pooled = this.acquireChip(r, hex(theme.palette.gold2));
    const chip = pooled.c;
    chip.position.set(x, y);

    // a brief "+$x" that floats up at the source while the coin flies off
    const label = makeText('+' + money(amount), { size: 14 + magnitude * 7, weight: '800', color: theme.color.goldText });
    label.anchor.set(0.5);
    const lw = label.width + 16, lh = label.height + 6;
    const labelBg = new Graphics();
    labelBg.roundRect(-lw / 2, -lh / 2, lw, lh, lh / 2).fill(hex(theme.palette.gold1));
    const tag = new Container();
    tag.position.set(x, y - r - 12);
    tag.addChild(labelBg, label);
    this.addChild(tag);
    if (sparkle) Juice.glowPing(this, x, y, hex(theme.palette.gold1), r + 8);

    Tweens.to({
      from: 0, to: 1, duration: 540, ease: easeOutQuart,
      onUpdate: (t) => { tag.position.set(x, y - r - 12 - t * 22); tag.alpha = t < 0.2 ? t / 0.2 : 1 - Math.max(0, (t - 0.5) / 0.5); },
      onComplete: () => tag.destroy({ children: true }),
    });

    // quadratic bezier arc to the wallet, with a small upward control bow
    const cx = (x + target.x) / 2;
    const cy = Math.min(x, target.y) - 60 - magnitude * 30;
    const delay = 180;
    Tweens.to({
      from: 0, to: 1, duration: 620, delay, ease: easeInOutCubic,
      onUpdate: (t) => {
        const mt = 1 - t;
        const bx = mt * mt * x + 2 * mt * t * cx + t * t * target.x;
        const by = mt * mt * y + 2 * mt * t * cy + t * t * target.y;
        chip.position.set(bx, by);
        chip.scale.set(1 - t * 0.45);
        chip.alpha = t > 0.85 ? 1 - (t - 0.85) / 0.15 : 1;
      },
      onComplete: () => { this.releaseChip(pooled); onArrive?.(); },
    });
  }

  /** Soft NEUTRAL ripple for a zero reveal. Honest framing: a tap that paid nothing must not
   *  read like a win-flavoured cue (UKGC LDW guidance). Plain gray, gentle, no rising sparkle. */
  emptyRipple(x: number, y: number): void {
    const g = new Graphics();
    g.position.set(x, y);
    this.addChild(g);
    Tweens.to({
      from: 0,
      to: 1,
      duration: 460,
      ease: easeOutCubic,
      onUpdate: (t) => {
        g.clear();
        g.circle(0, 0, 6 + t * 18).stroke({
          width: 2 * (1 - t),
          color: theme.color.muted,
          alpha: 0.32 * (1 - t),
        });
      },
      onComplete: () => g.destroy(),
    });
  }
}
